import { z } from "zod";
import type { KnowledgeCard, RecallCoordinate } from "../types/journey";

/** 三次回望与精神坐标的固定对应关系（与 JourneyRecall 的顺序一致）。 */
export const RECALL_THEMES = ["first", "struggle", "dedication"] as const;
export const RECALL_LABELS = ["首创", "奋斗", "奉献"] as const;
export type RecallStep = 0 | 1 | 2;

/** 后端 AI 回望输出的结构：小标题 + 回望正文 + 展开后细读的阐释。 */
export const RecallCoordinateSchema = z.object({
  headline: z.string().min(2).max(24),
  connection: z.string().min(40).max(140),
  elaboration: z.string().min(40).max(240),
});

export const RecallRequestSchema = z.object({
  sessionId: z.string().min(8).max(120),
  step: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  originalQuestion: z.string().min(5).max(120),
  firstAnswer: z.string().min(1).max(120),
  secondAnswer: z.string().min(1).max(120),
  thirdAnswer: z.string().min(1).max(80),
});
export type RecallRequest = z.infer<typeof RecallRequestSchema>;

/** 送给模型 / 本地兜底的完整输入：请求字段 + 这一次的回望对象与审核原文。 */
export interface RecallInput extends RecallRequest {
  label: string;
  focus: string;
  coordinateTitle: string;
  coordinateContent: string;
  coordinateQuote: string;
}

export function recallFocus(input: RecallRequest): string {
  return [input.firstAnswer, input.secondAnswer, input.thirdAnswer][input.step] ?? "";
}

/** 这一步要读的那份精神坐标：只认已发布、生产环境的卡片，取版本号最高的一张。 */
export function recallCoordinateFor(cards: KnowledgeCard[], step: number): KnowledgeCard | undefined {
  return cards
    .filter((card) => card.review_status === "published" && card.environment === "production")
    .filter((card) => card.theme === RECALL_THEMES[step])
    .sort((a, b) => b.version - a.version)[0];
}

/**
 * 本地兜底：不依赖模型也能给出与这一步回答相关的回望。
 * 展开细读的正文退回审核原文本身（与接入 AI 之前的表现一致）；
 * 事实层只用审核过的原文或页面落版金句，不新增任何党史事实。
 */
export function fallbackRecallCoordinate(input: RecallInput): RecallCoordinate {
  const focus = input.focus.replace(/[。！？!?]+$/u, "").slice(0, 18);
  const quote = input.coordinateQuote.trim().slice(0, 54);
  const headline = `${input.label}，就在这一步里`.slice(0, 24);
  const connection = [
    `你在这一步留下的是“${focus}”。`,
    `把它放回「${input.coordinateTitle || input.label}」来看：`,
    quote || "这一步不需要立刻有答案，先记住你当时为什么这样选，再让下一次选择比这一次更清楚。",
  ].join("");
  const reviewed = input.coordinateContent.trim();
  const parts = [reviewed, quote, "先让这一步的实施比解释更重要：把选择留在行动里，记下变化，再回头校准方向与节奏。"]
    .filter((part, index, list) => Boolean(part) && list.indexOf(part) === index);
  let elaboration = "";
  for (const part of parts) {
    elaboration = elaboration ? `${elaboration}${part}` : part;
    if (elaboration.length >= 40) break;
  }
  return { headline, connection: connection.slice(0, 140), elaboration: elaboration.slice(0, 240) };
}
